import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import { UserCheck, Users, Search, Settings } from "lucide-react";
import {
  getDoctorAttendanceToday,
  getDoctorAttendanceHistory,
  checkInDoctor,
  checkOutDoctor,
  getDoctors,
} from "../../api/api";
import { getSocket } from "../../lib/socket";
import { useSelector } from "react-redux";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Input } from "../../components/ui/input";
import SegmentedControl from "../../components/SegmentedControl";
import ProviderSelect from "../../components/ProviderSelect";
import DataTable from "../../components/DataTable";
import DoctorDrawer from "../../components/DoctorDrawer";
import MarkDelayModal from "../../components/MarkDelayModal";
import { cn } from "../../lib/utils";

const TABS = [
  { value: "today", label: "Today" },
  { value: "history", label: "History" },
];

const initialsOf = (name) =>
  (name || "")
    .replace("Dr ", "")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatDuration = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "—";
  const mins = moment(toDate).diff(moment(fromDate), "minutes");
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function TodayView({ isLogin }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [search, setSearch] = useState("");
  const [drawerDoctorId, setDrawerDoctorId] = useState(null);
  const [delayDoctor, setDelayDoctor] = useState(null);
  const [drawerRefreshToken, setDrawerRefreshToken] = useState(0);

  const load = useCallback(() => {
    getDoctorAttendanceToday()
      .then((res) => setDoctors(res.data))
      .catch((err) => toast.error(err?.message || "Could not load doctor attendance."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates when another tab/device checks a doctor in or out.
  useEffect(() => {
    if (!isLogin) return;
    const socket = getSocket();
    if (!socket) return;

    const onUpdate = () => load();
    socket.on("doctor_attendance:updated", onUpdate);
    return () => socket.off("doctor_attendance:updated", onUpdate);
  }, [isLogin, load]);

  // Safety poll — belt-and-braces in case a socket event is ever dropped;
  // staff tend to keep this page open all day.
  useEffect(() => {
    if (!isLogin) return;
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, [isLogin, load]);

  const runAction = async (fn, doctor, successMsg) => {
    setPendingId(doctor._id);
    try {
      await fn(doctor._id);
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(err?.message || "Action failed.");
    } finally {
      setPendingId(null);
    }
  };

  const checkedInCount = doctors.filter((d) => d.checked_in_at && !d.checked_out_at).length;

  const q = search.trim().toLowerCase();
  const matchesSearch = (d) =>
    !q ||
    d.name?.toLowerCase().includes(q) ||
    d.specialty?.toLowerCase().includes(q) ||
    d.department_id?.name?.toLowerCase().includes(q);

  // Whoever's actually in the building right now belongs at the top of the
  // desk's view; doctors already done for the day sink to the bottom.
  const statusRank = (d) => {
    if (d.checked_in_at && !d.checked_out_at) return 0; // checked in
    if (!d.on_leave && !d.checked_in_at) return 1; // not checked in yet
    if (d.on_leave) return 2;
    return 3; // checked out
  };

  // With no search active, only show doctors actually expected today — a
  // clinic with dozens of doctors on staggered weekly schedules would
  // otherwise bury who's actually relevant behind a wall of "Not checked in"
  // cards for people who simply aren't rostered today. Searching bypasses
  // this entirely (e.g. a doctor covering unexpectedly), and once a doctor
  // has any activity today they stay visible even if you clear the search.
  const isRelevantToday = (d) => d.scheduled_today || !!d.checked_in_at || !!d.checked_out_at;

  const filteredDoctors = doctors
    .filter(matchesSearch)
    .filter((d) => q || isRelevantToday(d))
    .slice()
    .sort((a, b) => statusRank(a) - statusRank(b) || a.name.localeCompare(b.name));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-stretch gap-4">
        <div className="relative min-w-[240px] flex-1 self-center">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by doctor name, specialty, or department…"
            className="h-11 pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Card className="w-full sm:w-56">
          <CardContent className="flex items-center gap-3 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
              <UserCheck className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">Checked In Now</p>
              <p className="font-serif text-xl font-semibold">{checkedInCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full sm:w-56">
          <CardContent className="flex items-center gap-3 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Users className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">{q ? "Matching Doctors" : "Expected Today"}</p>
              <p className="font-serif text-xl font-semibold">{filteredDoctors.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading doctors…</p>
      ) : filteredDoctors.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {doctors.length === 0
            ? "No doctors found."
            : q
            ? "No doctors match your search."
            : "No doctors are scheduled today. Search by name to find someone covering unexpectedly."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredDoctors.map((doctor) => {
            const isPending = pendingId === doctor._id;
            const isIn = !!doctor.checked_in_at && !doctor.checked_out_at;
            const isOut = !!doctor.checked_out_at;

            // One color per state, reused consistently for the accent bar,
            // the status dot, the badge, and the action button — so a card's
            // whole visual identity reads as a single deliberate color, not
            // three unrelated ones competing for attention.
            const accentBorder = doctor.on_leave
              ? "border-l-destructive"
              : isIn
              ? "border-l-secondary"
              : "border-l-transparent";

            return (
              <Card key={doctor._id} className={cn("border-l-4 transition-shadow hover:shadow-md", accentBorder)}>
                <CardContent className="space-y-4 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="relative shrink-0">
                        <Avatar className="h-11 w-11">
                          <AvatarFallback>{initialsOf(doctor.name)}</AvatarFallback>
                        </Avatar>
                        {isIn && (
                          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-secondary" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium leading-tight">{doctor.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[doctor.department_id?.name, doctor.specialty].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Manage leave and delay"
                      title="Manage leave and delay"
                      onClick={() => setDrawerDoctorId(doctor._id)}
                      className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {doctor.on_leave ? (
                      <Badge variant="destructive">On leave today</Badge>
                    ) : isIn ? (
                      <>
                        <Badge variant="secondary">Checked in</Badge>
                        <span className="text-xs text-muted-foreground">
                          since {moment(doctor.checked_in_at).format("h:mm A")}
                          {doctor.checked_in_by_name && ` · ${doctor.checked_in_by_name}`}
                        </span>
                      </>
                    ) : isOut ? (
                      <>
                        <Badge variant="outline">Checked out</Badge>
                        <span className="text-xs text-muted-foreground">
                          at {moment(doctor.checked_out_at).format("h:mm A")}
                          {doctor.checked_out_by_name && ` · ${doctor.checked_out_by_name}`}
                        </span>
                      </>
                    ) : (
                      <Badge variant="outline" className="border-dashed text-muted-foreground">
                        Not checked in
                      </Badge>
                    )}
                  </div>

                  {!doctor.on_leave && (
                    <Button
                      className="w-full"
                      variant={isIn ? "secondary" : "default"}
                      disabled={isPending}
                      onClick={() =>
                        isIn
                          ? runAction(checkOutDoctor, doctor, `${doctor.name} checked out.`)
                          : runAction(checkInDoctor, doctor, `${doctor.name} checked in.`)
                      }
                    >
                      {isPending ? "…" : isIn ? "Check Out" : isOut ? "Check In Again" : "Check In"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DoctorDrawer
        doctorId={drawerDoctorId}
        onClose={() => setDrawerDoctorId(null)}
        onMarkDelay={(d) => setDelayDoctor(d)}
        refreshToken={drawerRefreshToken}
      />
      <MarkDelayModal
        open={!!delayDoctor}
        onOpenChange={(v) => !v && setDelayDoctor(null)}
        doctor={delayDoctor}
        onSaved={() => {
          setDelayDoctor(null);
          setDrawerRefreshToken((t) => t + 1);
          load();
        }}
      />
    </div>
  );
}

function HistoryView() {
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [from, setFrom] = useState(() => moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => moment().format("YYYY-MM-DD"));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });

  useEffect(() => {
    getDoctors({ limit: 200, include_inactive: true })
      .then((res) => setDoctors(res.data))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getDoctorAttendanceHistory({
      doctor_id: doctorId || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      limit: 15,
    })
      .then((res) => {
        setRecords(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load attendance history."))
      .finally(() => setLoading(false));
  }, [doctorId, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: "doctor",
      header: "Doctor",
      render: (r) => (
        <div>
          <p className="font-medium">{r.doctor_id?.name}</p>
          <p className="text-xs text-muted-foreground">{r.doctor_id?.specialty}</p>
        </div>
      ),
    },
    { key: "date", header: "Date", render: (r) => moment(r.iso_date).format("D MMM YYYY") },
    {
      key: "checked_in",
      header: "Checked In",
      render: (r) =>
        r.checked_in_at ? (
          <div>
            <p>{moment(r.checked_in_at).format("h:mm A")}</p>
            {r.checked_in_by?.name && <p className="text-xs text-muted-foreground">by {r.checked_in_by.name}</p>}
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "checked_out",
      header: "Checked Out",
      render: (r) =>
        r.checked_out_at ? (
          <div>
            <p>{moment(r.checked_out_at).format("h:mm A")}</p>
            {r.checked_out_by?.name && <p className="text-xs text-muted-foreground">by {r.checked_out_by.name}</p>}
          </div>
        ) : (
          "—"
        ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (r) => formatDuration(r.checked_in_at, r.checked_out_at),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ProviderSelect doctors={doctors} value={doctorId} onChange={(v) => { setDoctorId(v); setPage(1); }} className="w-64" />
        <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="h-9 w-40" />
        <span className="text-sm text-muted-foreground">to</span>
        <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="h-9 w-40" />
      </div>

      <DataTable
        columns={columns}
        data={records}
        loading={loading}
        emptyMessage="No attendance records in this range."
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        rowKey={(r) => r._id}
      />
    </div>
  );
}

export default function DoctorAttendance() {
  const isLogin = useSelector((s) => s.users.is_login);
  const [tab, setTab] = useState("today");
  const [now, setNow] = useState(() => moment());

  // Live clock — also doubles as the "this page is live" cue for the desk.
  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl font-semibold">Doctor Attendance</h2>
          <p className="text-sm text-muted-foreground">Check doctors in and out, and review past attendance.</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <SegmentedControl items={TABS} value={tab} onChange={setTab} />
          <p className="text-xs text-muted-foreground">{now.format("dddd · D MMMM YYYY · h:mm:ss A")}</p>
        </div>
      </div>

      {tab === "today" ? <TodayView isLogin={isLogin} /> : <HistoryView />}
    </div>
  );
}
